import React, { useEffect, useCallback, useRef } from 'react';
import styles from 'features/profile/index.module.scss';
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import LoadingCard from 'componets/loading-card';
import ProfileEmail from 'features/profile/profile-email';
import ProfileChangePassword from 'features/profile/profile-change-password';
import {
  CHANGE_PASSWORD_EDIT_MODE,
  CLOSE_IN_EDIT_MODE,
  EMAIL_EDIT_MODE,
  OPEN_IN_EDIT_MODE,
  useProfileContext,
} from 'features/profile/use-profile';
import { CHANGE_CURRENT_PAGE, PAGE_PROFILE, useAppContext } from 'use-app';
import UseDevice, { DEVICE_MOBILE } from 'hooks/use-device';
import analytics from 'service/analytics';
import { ProfileAccountSettingSection } from './profile-account-setting-section';
import { OnStreetParkingInformation } from './on-street-parking-information';
import { TaxIdNumberSection } from './tax-id-number-section';
import { useEnterpriseTaxSettings } from 'service/use-enterprise-tax-settings';

const Profile = () => {
  const { dispatch: appDispatch } = useAppContext();
  const { state, dispatch, isMainAccount, me } = useProfileContext();
  const editingFormRef = useRef();
  const device = UseDevice();
  const { enterpriseTaxSettings } = useEnterpriseTaxSettings();

  useEffect(() => {
    window.scrollTo(0, 0);
    analytics.track('帳號資訊頁_pageview');
  }, []);

  useEffect(() => {
    if (device !== DEVICE_MOBILE) return;
    if (state.whichInEditMode) {
      disableBodyScroll(editingFormRef.current, {
        allowTouchMove: el => true,
      });
    } else {
      enableBodyScroll(editingFormRef.current);
    }
    return () => {
      clearAllBodyScrollLocks();
    };
  }, [device, state.whichInEditMode]);

  useEffect(() => {
    appDispatch({
      type: CHANGE_CURRENT_PAGE,
      payload: PAGE_PROFILE,
    });
  }, [appDispatch]);

  const onCloseEditMode = useCallback(() => {
    dispatch({ type: CLOSE_IN_EDIT_MODE });
  }, [dispatch]);

  const onOpenEditMode = useCallback(
    which => {
      dispatch({
        type: OPEN_IN_EDIT_MODE,
        payload: which,
      });
    },
    [dispatch]
  );

  const ProfileContainer = () => {
    if (me) {
      return <ProfileContent />;
    }

    return <LoadingCard />;
  };

  const setEditingFormRef = useCallback(
    (el, editMode) => {
      if (state.whichInEditMode === editMode) {
        editingFormRef.current = el;
      }
    },
    [state.whichInEditMode]
  );

  const ProfileContent = () => {
    const email = isMainAccount ? me.email : me.subAccount.email;
    return (
      <>
        <ProfileAccountSettingSection user={me} isMainAccount={isMainAccount} />
        <ProfileEmail
          ref={ref => setEditingFormRef(ref, EMAIL_EDIT_MODE)}
          email={email}
          inEditMode={state.whichInEditMode === EMAIL_EDIT_MODE}
          onOpenEditMode={onOpenEditMode}
          onCloseEditMode={onCloseEditMode}
        />
        <OnStreetParkingInformation enableOnStreetParkingAutoPay={!!me?.enableOnStreetParkingAutoPay} />
        <TaxIdNumberSection enterpriseTaxSettings={enterpriseTaxSettings} />
        <ProfileChangePassword
          ref={ref => setEditingFormRef(ref, CHANGE_PASSWORD_EDIT_MODE)}
          inEditMode={state.whichInEditMode === CHANGE_PASSWORD_EDIT_MODE}
          onOpenEditMode={onOpenEditMode}
          onCloseEditMode={onCloseEditMode}
        />
      </>
    );
  };

  return (
    <div className={styles.main}>
      <div className={styles.title}>企業帳號</div>
      <div className={styles.desc}>您在企業方案設定的基本資訊</div>
      <div className={styles.content}>
        <ProfileContainer />
      </div>
    </div>
  );
};

const ProfilePage = () => {
  return (
    <useProfileContext.Provider>
      <Profile />
    </useProfileContext.Provider>
  );
};

export default ProfilePage;
